import React, { useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import { showSuccess, showError } from '../../../components/ModernSnackbar';

const AddUserModal = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    email: '',
    userType: 'SCHOOL', // Changed default to SCHOOL
    course: '',
    college: '',
    grade: '',
    section: '',
    school: '',
    company: '',
    role: '',
    education: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    // Input restrictions based on field type
    let processedValue = value;
    
    switch (field) {
      case 'name':
        // Only allow letters and spaces
        processedValue = value.replace(/[^a-zA-Z\s]/g, '');
        break;
      case 'age':
        // Only allow numbers
        processedValue = value.replace(/[^0-9]/g, '');
        break;
      case 'phone':
        // Only allow numbers, spaces, dashes, and parentheses
        processedValue = value.replace(/[^0-9\s\-\(\)]/g, '');
        break;
      case 'grade':
        // Only allow numbers
        processedValue = value.replace(/[^0-9]/g, '');
        break;
      case 'section':
        // Only allow single uppercase letter
        processedValue = value.replace(/[^A-Z]/g, '').toUpperCase().slice(0, 1);
        break;
      default:
        processedValue = value;
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    console.log('AddUserModal: validateForm called');
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters long';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Name must be less than 50 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name can only contain letters and spaces';
    }

    // Age validation
    if (!formData.age.trim()) {
      newErrors.age = 'Age is required';
    } else {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum)) {
        newErrors.age = 'Age must be a valid number';
      } else if (ageNum < 5 || ageNum > 100) {
        newErrors.age = 'Age must be between 5 and 100';
      }
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Remove spaces, dashes, and parentheses for validation
      const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
      if (!/^\d{10}$/.test(cleanPhone)) {
        newErrors.phone = 'Phone number must be 10 digits';
      }
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      } else if (formData.email.length > 100) {
        newErrors.email = 'Email must be less than 100 characters';
      }
    }

    // User type specific validation
    if (formData.userType === 'COLLEGE') {
      if (!formData.course.trim()) {
        newErrors.course = 'Course is required';
      } else if (formData.course.trim().length < 2) {
        newErrors.course = 'Course must be at least 2 characters long';
      } else if (formData.course.trim().length > 100) {
        newErrors.course = 'Course must be less than 100 characters';
      }
      
      if (!formData.college.trim()) {
        newErrors.college = 'College is required';
      } else if (formData.college.trim().length < 2) {
        newErrors.college = 'College name must be at least 2 characters long';
      } else if (formData.college.trim().length > 200) {
        newErrors.college = 'College name must be less than 200 characters';
      }
    } else if (formData.userType === 'SCHOOL') {
      if (!formData.grade.trim()) {
        newErrors.grade = 'Grade is required';
      } else if (!/^[0-9]{1,2}$/.test(formData.grade.trim())) {
        newErrors.grade = 'Grade must be a number between 1-12';
      }
      
      if (!formData.section.trim()) {
        newErrors.section = 'Section is required';
      } else if (!/^[A-Z]$/.test(formData.section.trim())) {
        newErrors.section = 'Section must be a single letter (A-Z)';
      }
      
      if (!formData.school.trim()) {
        newErrors.school = 'School is required';
      } else if (formData.school.trim().length < 2) {
        newErrors.school = 'School name must be at least 2 characters long';
      } else if (formData.school.trim().length > 200) {
        newErrors.school = 'School name must be less than 200 characters';
      }
    } else if (formData.userType === 'PROFESSIONAL') {
      if (!formData.company.trim()) {
        newErrors.company = 'Company is required';
      } else if (formData.company.trim().length < 2) {
        newErrors.company = 'Company name must be at least 2 characters long';
      } else if (formData.company.trim().length > 200) {
        newErrors.company = 'Company name must be less than 200 characters';
      }
      
      if (!formData.role.trim()) {
        newErrors.role = 'Role is required';
      } else if (formData.role.trim().length < 2) {
        newErrors.role = 'Role must be at least 2 characters long';
      } else if (formData.role.trim().length > 100) {
        newErrors.role = 'Role must be less than 100 characters';
      }
      
      if (!formData.education.trim()) {
        newErrors.education = 'Education is required';
      } else if (formData.education.trim().length < 2) {
        newErrors.education = 'Education must be at least 2 characters long';
      } else if (formData.education.trim().length > 100) {
        newErrors.education = 'Education must be less than 100 characters';
      }
    }

    console.log('AddUserModal: Validation errors:', newErrors);
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log('AddUserModal: Form is valid:', isValid);
    return isValid;
  };

  const handleSubmit = async () => {
    console.log('AddUserModal: handleSubmit called');
    console.log('AddUserModal: formData:', formData);
    
    if (validateForm()) {
      console.log('AddUserModal: Form validation passed');
      setLoading(true);
      try {
        console.log('AddUserModal: Calling onSubmit with formData');
        await onSubmit(formData);
        console.log('AddUserModal: onSubmit completed successfully');
        handleClose();
      } catch (error) {
        // Error handling is done in the parent component
        console.error('Error in modal submit:', error);
      } finally {
        setLoading(false);
      }
    } else {
      console.log('AddUserModal: Form validation failed');
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      age: '',
      phone: '',
      email: '',
      userType: 'SCHOOL', // Reset to SCHOOL default
      course: '',
      college: '',
      grade: '',
      section: '',
      school: '',
      company: '',
      role: '',
      education: '',
    });
    setErrors({});
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-user-modal-title"
      aria-describedby="add-user-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 600 },
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflow: 'auto'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            {loading ? 'Adding User...' : 'Add New User'}
          </Typography>
          {loading && <CircularProgress size={24} />}
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Basic Information */}
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Basic Information
            </Typography>
            
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name || 'Enter full name (letters only)'}
                sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                inputProps={{
                  maxLength: 50
                }}
              />
              
              <TextField
                label="Age"
                value={formData.age}
                onChange={(e) => handleChange('age', e.target.value)}
                fullWidth
                required
                error={!!errors.age}
                helperText={errors.age || 'Enter age (5-100)'}
                sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                inputProps={{
                  maxLength: 3
                }}
              />
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                fullWidth
                required
                error={!!errors.phone}
                helperText={errors.phone || 'Enter 10-digit phone number'}
                sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                inputProps={{
                  maxLength: 15
                }}
              />
              
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email || 'Enter valid email address'}
                sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                inputProps={{
                  maxLength: 100
                }}
              />
            </Box>
          </Box>

          {/* User Type Selection */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              User Type
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                row
                value={formData.userType}
                onChange={(e) => handleChange('userType', e.target.value)}
                sx={{
                  gap: 3,
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-around'
                }}
              >
                <FormControlLabel
                  value="COLLEGE"
                  control={<Radio />}
                  label="College"
                />
                <FormControlLabel
                  value="SCHOOL"
                  control={<Radio />}
                  label="School"
                />
                <FormControlLabel
                  value="PROFESSIONAL"
                  control={<Radio />}
                  label="Professional"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Dynamic Fields Based on Selected Option */}
          {formData.userType === 'COLLEGE' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                College Information
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Course"
                  value={formData.course}
                  onChange={(e) => handleChange('course', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.course}
                  helperText={errors.course || 'Enter course name'}
                  sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                  inputProps={{
                    maxLength: 100
                  }}
                />
                <TextField
                  label="College Name"
                  value={formData.college}
                  onChange={(e) => handleChange('college', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.college}
                  helperText={errors.college || 'Enter college name'}
                  sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                  inputProps={{
                    maxLength: 200
                  }}
                />
              </Box>
            </Box>
          )}

          {formData.userType === 'SCHOOL' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                School Information
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Grade"
                  value={formData.grade}
                  onChange={(e) => handleChange('grade', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.grade}
                  helperText={errors.grade || 'Enter grade (1-12)'}
                  sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                  inputProps={{
                    maxLength: 2
                  }}
                />
                <TextField
                  label="Section"
                  value={formData.section}
                  onChange={(e) => handleChange('section', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.section}
                  helperText={errors.section || 'Enter section (A-Z)'}
                  sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                  inputProps={{
                    maxLength: 1
                  }}
                />
              </Box>
              <TextField
                label="School Name"
                value={formData.school}
                onChange={(e) => handleChange('school', e.target.value)}
                fullWidth
                required
                error={!!errors.school}
                helperText={errors.school || 'Enter school name'}
                sx={{ mt: 2 }}
                inputProps={{
                  maxLength: 200
                }}
              />
            </Box>
          )}

          {formData.userType === 'PROFESSIONAL' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Professional Information
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Company Name"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.company}
                  helperText={errors.company || 'Enter company name'}
                  sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                  inputProps={{
                    maxLength: 200
                  }}
                />
                <TextField
                  label="Role"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  fullWidth
                  required
                  error={!!errors.role}
                  helperText={errors.role || 'Enter job role'}
                  sx={{ minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}
                  inputProps={{
                    maxLength: 100
                  }}
                />
              </Box>
              <TextField
                label="Highest Education"
                value={formData.education}
                onChange={(e) => handleChange('education', e.target.value)}
                fullWidth
                required
                error={!!errors.education}
                helperText={errors.education || 'Enter highest education level'}
                sx={{ mt: 2 }}
                inputProps={{
                  maxLength: 100
                }}
              />
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={1} mt={3}>
          <Button
            variant="outlined"
            onClick={handleClose}
            fullWidth
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={loading}
          >
            {loading ? 'Adding User...' : 'Add User'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddUserModal; 